import {Injectable} from '@angular/core';
import {Hex} from 'viem';
import {Deliverable} from '../../components/dex/dex-streaming/types/deliverable';
import {
    TxHistoryDepositMeta,
    TxHistoryRec,
    TxHistoryRecMethod,
    TxHistoryStatus,
    TxHistoryTransferMeta,
    TxHistoryWithdrawMeta
} from './tx-history-rec';

@Injectable({providedIn: 'root'})
export class TxHistoryRecFactory {

    public createForNewDeposit(params: {
        hash: string;
        from: string;
        to: string;
        chainId: number;
        trancheId: bigint | number | string;
        deliverableId: bigint | number | string;
        assetQtyToDeposit: bigint | number | string;
        minLptQty: bigint | number | string;
        clientReference: Hex;
    }): TxHistoryRec {
        const ts = Date.now();

        return {
            hash:                 params.hash,
            from:                 params.from,
            to:                   params.to,
            data:                 undefined,
            value:                undefined,
            nonce:                undefined,
            gasLimit:             undefined,
            gasPrice:             undefined,
            maxFeePerGas:         undefined,
            maxPriorityFeePerGas: undefined,
            chainId:              params.chainId,
            blockNumber:          undefined,
            status:               TxHistoryStatus.PENDING,
            createdAt:            ts,
            updatedAt:            ts,
            metadata:             this.createMetaForDeposit(params)
        };
    }

    public createForNewWithdrawal(params: {
        hash: string;
        from: string;
        to: string;
        chainId: number;
        trancheId: bigint | number | string;
        lptQuantityToBurn: bigint | number | string;
        minAssetValidUsd: bigint | number | string;
        preferredDeliverableId: bigint | number | string;
        minAcceptablePreferredAssetQty: bigint | number | string;
        clientReference: Hex;
    }): TxHistoryRec {
        const ts = Date.now();

        return {
            hash:                 params.hash,
            from:                 params.from,
            to:                   params.to,
            data:                 undefined,
            value:                undefined,
            nonce:                undefined,
            gasLimit:             undefined,
            gasPrice:             undefined,
            maxFeePerGas:         undefined,
            maxPriorityFeePerGas: undefined,
            chainId:              params.chainId,
            blockNumber:          undefined,
            status:               TxHistoryStatus.PENDING,
            createdAt:            ts,
            updatedAt:            ts,
            metadata:             this.createMetaForWithdrawal(params)
        };
    }

    public createForNewTransfer(params: {
        hash: string;
        from: string;
        to: string;
        chainId: number;
        deliverable: Deliverable;
        amount: bigint | number | string;
    }): TxHistoryRec {
        const ts = Date.now();

        return {
            hash:                 params.hash,
            from:                 params.from,
            to:                   params.to,
            data:                 undefined,
            value:                undefined,
            nonce:                undefined,
            gasLimit:             undefined,
            gasPrice:             undefined,
            maxFeePerGas:         undefined,
            maxPriorityFeePerGas: undefined,
            chainId:              params.chainId,
            blockNumber:          undefined,
            status:               TxHistoryStatus.PENDING,
            createdAt:            ts,
            updatedAt:            ts,
            metadata:             this.createMetaForTransfer(params)
        };
    }

    private createMetaForDeposit(params: {
        trancheId: bigint | number | string;
        deliverableId: bigint | number | string;
        assetQtyToDeposit: bigint | number | string;
        minLptQty: bigint | number | string;
        clientReference: Hex;
    }): TxHistoryDepositMeta {
        return {
            method: TxHistoryRecMethod.DEPOSIT,
            trancheId: Number(params.trancheId),
            deliverableId: Number(params.deliverableId),
            assetQtyToDeposit: String(params.assetQtyToDeposit),
            minLptQty: String(params.minLptQty),
            clientReference: params.clientReference
        };
    }

    private createMetaForWithdrawal(params: {
        trancheId: bigint | number | string;
        lptQuantityToBurn: bigint | number | string;
        minAssetValidUsd: bigint | number | string;
        preferredDeliverableId: bigint | number | string;
        minAcceptablePreferredAssetQty: bigint | number | string;
        clientReference: Hex;
    }): TxHistoryWithdrawMeta {
        return {
            method: TxHistoryRecMethod.WITHDRAW,
            trancheId: Number(params.trancheId),
            lptQuantityToBurn: String(params.lptQuantityToBurn),
            minAssetValidUsd: String(params.minAssetValidUsd),
            preferredDeliverableId: Number(params.preferredDeliverableId),
            minAcceptablePreferredAssetQty: String(params.minAcceptablePreferredAssetQty),
            clientReference: params.clientReference
        };
    }

    private createMetaForTransfer(params: {
        deliverable: Deliverable;
        amount: bigint | number | string;
        from: string;
        to: string;
    }): TxHistoryTransferMeta {
        return {
            method:        TxHistoryRecMethod.TRANSFER,
            deliverableId: Number(params.deliverable.deliverableId),
            tokenAddress:  params.deliverable.identifier,
            amount:        String(params.amount),
            fromWallet:    params.from,
            toWallet:      params.to
        };
    }

}
